import { useState, useEffect } from 'react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { format } from 'date-fns'
import { getRadioHistory, HistoryPoint } from '../services/api'

const WINDOWS = [
  { label: '1h', hours: 1 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
]

interface MetricDef {
  key: string
  field: keyof HistoryPoint
  label: string
  unit: 'mbps' | 'db'   // Mbps → left axis, dB → right axis
  color: string
}

// Capacity/throughput are Mbps (kbps/1000); path loss + SNR/SINR are dB.
const METRICS: MetricDef[] = [
  { key: 'dlCap', field: 'dl_capacity_kbps', label: 'DL cap', unit: 'mbps', color: '#2563eb' },
  { key: 'ulCap', field: 'ul_capacity_kbps', label: 'UL cap', unit: 'mbps', color: '#16a34a' },
  { key: 'dlRate', field: 'dl_rate_kbps', label: 'DL rate', unit: 'mbps', color: '#0891b2' },
  { key: 'ulRate', field: 'ul_rate_kbps', label: 'UL rate', unit: 'mbps', color: '#65a30d' },
  { key: 'pathLoss', field: 'path_loss', label: 'Path loss', unit: 'db', color: '#dc2626' },
  { key: 'dlSnr', field: 'dl_snr', label: 'DL SNR', unit: 'db', color: '#d97706' },
  { key: 'ulSnr', field: 'ul_snr', label: 'UL SNR', unit: 'db', color: '#ea580c' },
  { key: 'dlSinr', field: 'dl_sinr', label: 'DL SINR', unit: 'db', color: '#9333ea' },
  { key: 'ulSinr', field: 'ul_sinr', label: 'UL SINR', unit: 'db', color: '#7c3aed' },
]
const METRIC_KEYS = new Set(METRICS.map((m) => m.key))

const LS_METRICS = 'linkHistory.metrics'
const LS_HOURS = 'linkHistory.hours'
const DEFAULT_METRICS = ['dlCap', 'ulCap']

function loadSelected(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_METRICS)
    if (raw) {
      const arr = JSON.parse(raw)
      if (Array.isArray(arr)) {
        const valid = arr.filter((k: unknown) => typeof k === 'string' && METRIC_KEYS.has(k))
        if (valid.length) return new Set(valid as string[])
      }
    }
  } catch {
    /* private mode / cleared storage — fall through to default */
  }
  return new Set(DEFAULT_METRICS)
}

function loadHours(): number {
  try {
    const n = parseInt(localStorage.getItem(LS_HOURS) || '', 10)
    if ([1, 24, 168].includes(n)) return n
  } catch {
    /* ignore */
  }
  return 24
}

interface ChartRow {
  t: number
  [key: string]: number | null
}

export default function LinkHistoryChart({ serial }: { serial: string }) {
  const [hours, setHours] = useState<number>(loadHours)
  const [selected, setSelected] = useState<Set<string>>(loadSelected)
  const [rows, setRows] = useState<ChartRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Persist choices so they survive re-expanding a row / a page reload.
  useEffect(() => {
    try {
      localStorage.setItem(LS_METRICS, JSON.stringify([...selected]))
    } catch {
      /* ignore */
    }
  }, [selected])
  useEffect(() => {
    try {
      localStorage.setItem(LS_HOURS, String(hours))
    } catch {
      /* ignore */
    }
  }, [hours])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getRadioHistory(serial, hours)
      .then((r) => {
        if (cancelled) return
        setRows(
          r.data.points.map((p: HistoryPoint) => {
            const row: ChartRow = { t: new Date(p.t).getTime() }
            for (const m of METRICS) {
              const raw = p[m.field] as number | null
              row[m.key] = raw == null ? null : m.unit === 'mbps' ? Math.round((raw / 1000) * 10) / 10 : raw
            }
            return row
          })
        )
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load history')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [serial, hours])

  const fmtX = (t: number) => {
    const d = new Date(t)
    return hours <= 24 ? format(d, 'HH:mm') : format(d, 'EEE HH:mm')
  }

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const shown = METRICS.filter((m) => selected.has(m.key))
  const hasMbps = shown.some((m) => m.unit === 'mbps')
  const hasDb = shown.some((m) => m.unit === 'db')

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">Signal &amp; link history</h4>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w.hours}
              onClick={() => setHours(w.hours)}
              className={`px-2 py-0.5 text-xs rounded ${
                hours === w.hours ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Metric picker — click to show/hide a series (choices are remembered). */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {METRICS.map((m) => {
          const on = selected.has(m.key)
          return (
            <button
              key={m.key}
              onClick={() => toggle(m.key)}
              className="px-2 py-0.5 text-xs rounded border"
              style={
                on
                  ? { background: m.color, borderColor: m.color, color: '#fff' }
                  : { background: '#fff', borderColor: '#d1d5db', color: '#6b7280' }
              }
              title={m.unit === 'mbps' ? 'Mbps (left axis)' : 'dB (right axis)'}
            >
              {m.label}
            </button>
          )
        })}
      </div>

      {loading && <div className="text-xs text-gray-400">Loading…</div>}
      {error && <div className="text-xs text-red-500">{error}</div>}
      {!loading && !error && rows.length === 0 && (
        <div className="text-xs text-gray-400">No history for this window.</div>
      )}
      {!loading && !error && rows.length > 0 && shown.length === 0 && (
        <div className="text-xs text-gray-400">Select a metric above to plot.</div>
      )}

      {!loading && !error && rows.length > 0 && shown.length > 0 && (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="t" tickFormatter={fmtX} tick={{ fontSize: 10 }} minTickGap={40} />
            {hasMbps && (
              <YAxis yAxisId="mbps" orientation="left" tick={{ fontSize: 10 }}
                     label={{ value: 'Mbps', angle: -90, position: 'insideLeft', fontSize: 10 }} />
            )}
            {hasDb && (
              <YAxis yAxisId="db" orientation="right" tick={{ fontSize: 10 }}
                     label={{ value: 'dB', angle: 90, position: 'insideRight', fontSize: 10 }} />
            )}
            <Tooltip labelFormatter={(t) => fmtX(t as number)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {shown.map((m) => (
              <Line
                key={m.key}
                yAxisId={m.unit}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
